import { Logger } from '@frontend/logger';

export interface AndroidWebViewNetworkStatus {
    /** Whether the device has a local network link (Wi‑Fi / Ethernet / mobile data). */
    deviceConnected: boolean;
    /** Whether the device can reach the public internet. */
    networkConnected: boolean;
    /** Whether the device can reach the Telloport IoT backend. */
    backendConnected: boolean;
}

/**
 * Functions injected onto `window` by the native Android kiosk shell (GeckoView).
 * Add new bridge methods here as the native app exposes them.
 */
interface AndroidWebViewBridge {
    keepAlive(deviceId: string): void;
    getNetworkStatus(): AndroidWebViewNetworkStatus;
}

declare global {
    interface Window extends Partial<AndroidWebViewBridge> {}
}

function isAndroidWebViewNetworkStatus(value: unknown): value is AndroidWebViewNetworkStatus {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as AndroidWebViewNetworkStatus).deviceConnected === 'boolean' &&
        typeof (value as AndroidWebViewNetworkStatus).networkConnected === 'boolean' &&
        typeof (value as AndroidWebViewNetworkStatus).backendConnected === 'boolean'
    );
}

/**
 * Typed facade for the native Android WebView JavaScript interface.
 * Use {@link AndroidWebViewApp.getInstance} to access bridge methods from the web app.
 */
export class AndroidWebViewApp {
    private static instance: AndroidWebViewApp | null = null;

    private static readonly BRIDGE_METHODS: (keyof AndroidWebViewBridge)[] = ['keepAlive', 'getNetworkStatus'];

    private readonly reportedMissingMethods = new Set<keyof AndroidWebViewBridge>();

    static getInstance(): AndroidWebViewApp {
        AndroidWebViewApp.instance ??= new AndroidWebViewApp();
        return AndroidWebViewApp.instance;
    }

    /** Whether the native bridge exposes at least one expected method. */
    get isAvailable(): boolean {
        return typeof window.keepAlive === 'function';
    }

    /**
     * Logs once per missing method when a bridge call is attempted outside the Android shell.
     */
    reportMissingBridgeMethods(): void {
        for (const method of AndroidWebViewApp.BRIDGE_METHODS) {
            if (typeof window[method] !== 'function') {
                this.reportMissing(method);
            }
        }
    }

    keepAlive(deviceId: string): boolean {
        const bridgeMethod = window.keepAlive;

        if (typeof bridgeMethod !== 'function') {
            this.reportMissing('keepAlive');
            return false;
        }

        try {
            bridgeMethod(deviceId);
            return true;
        } catch (error) {
            Logger.warn('[android-webview] keepAlive failed', error);
            return false;
        }
    }

    getNetworkStatus(): AndroidWebViewNetworkStatus | null {
        const bridgeMethod = window.getNetworkStatus;

        if (typeof bridgeMethod !== 'function') {
            this.reportMissing('getNetworkStatus');
            return null;
        }

        try {
            const result = bridgeMethod();

            if (!isAndroidWebViewNetworkStatus(result)) {
                Logger.warn('[android-webview] getNetworkStatus returned invalid payload', result);
                return null;
            }

            return result;
        } catch (error) {
            Logger.warn('[android-webview] getNetworkStatus failed', error);
            return null;
        }
    }

    private reportMissing(method: keyof AndroidWebViewBridge): void {
        if (this.reportedMissingMethods.has(method)) {
            return;
        }

        this.reportedMissingMethods.add(method);
        Logger.log(`[android-webview] ${method} not available — running outside Android shell`);
    }
}
